/* Kkumi Club landing (v18 handoff) — one runtime for every kkumi-club-land-*
 * section on /pages/kkumi-club. Loaded `defer` by each section; boots once.
 *
 * Reads the rewards App Proxy through window.KKRewards (cached):
 *   /program   (public)  earn rate, bonuses, tiers, redeem rate, member count
 *   /balance   (signed in) balance, tier, birthday, club_member
 *   /options   (when a reward card names a reward id / group key)
 *   /referral  (Club members, refer section) referral_url + scoreboard
 *   /card      (signed in, earn section) the member's stamp card
 * and writes the live figures into the server-rendered fallbacks.
 *
 * Signed in is not the same as in the Club (club gate, business-logic.md):
 * Liquid only knows `customer`, so member-only markup is rendered for every
 * signed-in shopper and /balance `club_member: false` flips the page to the
 * signed-out view: [data-kkcl-member-only] hides, [data-kkcl-nonmember-only]
 * (join CTAs) shows, and the Club FAQ's guest-only questions come back.
 *
 * House rules this file keeps:
 *  - All copy comes from section settings via data-* on each section root;
 *    templates use {token}s and are rendered with text nodes (never innerHTML).
 *  - A value we don't have is hidden, never shown as a made-up zero.
 *  - Hooks are data-kkcl* attributes, so nothing collides with kkumi-club.js
 *    (still loaded by the Club hero) and sections stay reorderable.
 */
(function () {
  'use strict';
  if (window.__kkclInit) return;
  window.__kkclInit = true;

  var LOCALE = document.documentElement.lang || undefined;

  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function fmt(n) { return Math.round(Number(n) || 0).toLocaleString(LOCALE); }
  function fmtRate(n) {
    var r = Math.round(Number(n) * 10) / 10;
    return r % 1 ? r.toLocaleString(LOCALE, { minimumFractionDigits: 1 }) : fmt(r);
  }
  function money(dollars) {
    if (window.KKMoney) return window.KKMoney.fromDollars(dollars, { shop: true, trim: true });
    return '$' + dollars; // KKMoney is a head-loaded global; this is only a guard
  }
  function setText(el, v) { if (el && el.textContent !== v) el.textContent = v; }
  function show(el, on) { if (el) el.hidden = !on; }

  /* Render "{a} text {b}" into `el` as text nodes; a map value that is a DOM
   * Node is inserted as-is (e.g. an <em>), everything else as text. */
  function tpl(el, template, map) {
    if (!el) return;
    var frag = document.createDocumentFragment();
    String(template || '').split(/(\{[a-z_]+\})/).forEach(function (part) {
      var m = part.match(/^\{([a-z_]+)\}$/);
      var v = m && Object.prototype.hasOwnProperty.call(map, m[1]) ? map[m[1]] : null;
      if (v && v.nodeType) frag.appendChild(v);
      else if (v != null) frag.appendChild(document.createTextNode(String(v)));
      else if (part) frag.appendChild(document.createTextNode(part));
    });
    el.textContent = '';
    el.appendChild(frag);
  }
  function str(template, map) {
    return String(template || '').replace(/\{([a-z_]+)\}/g, function (all, k) {
      return Object.prototype.hasOwnProperty.call(map, k) ? String(map[k]) : all;
    });
  }
  var node = window.KKEl; // el(tag, cls, text), head-loaded before section scripts

  /* ---------------------------------------------------------------- data */
  // member: the /balance payload of a Club member (null for guests and for
  // signed-in non-members); nonMember: signed in but club_member === false.
  var state = { program: null, member: null, nonMember: false, balanceFailed: false, options: null, card: null };

  function isMemberPage() { return !!document.querySelector('[data-kkcl-section][data-kkcl-member="1"]'); }
  function get(path) {
    if (!window.KKRewards) return Promise.reject(new Error('no KKRewards'));
    return window.KKRewards.get(path);
  }

  function tiers() { return (state.program && state.program.tiers) || []; }
  function tierIndex(id) {
    var t = tiers();
    for (var i = 0; i < t.length; i++) if (t[i].id === id) return i;
    return -1;
  }
  /** The viewer's tier: the member's own, or the first tier for guests. */
  function viewer() {
    var t = tiers();
    if (!t.length) return null;
    var idx = 0;
    if (state.member && state.member.tier) {
      var i = tierIndex(state.member.tier.id);
      if (i < 0) return null; // unknown tier id: don't guess
      idx = i;
    }
    return { idx: idx, tier: t[idx] };
  }
  function earnRate(tier) {
    var p = state.program.points || {};
    return (Number(p.earn_per_dollar) || 0) * (Number(tier.multiplier) || 1);
  }
  function pointsToDollars(pts) {
    var p = state.program.points || {};
    if (!p.redeem_step) return null;
    return (pts / p.redeem_step) * (Number(p.redeem_dollars) || 0);
  }

  /* ------------------------------------------------------ program-wide */
  function applyProgram() {
    var prog = state.program;
    if (!prog) return;
    var p = prog.points || {};
    if (p.redeem_step) {
      $all('[data-kkcl="redeem-pts"]').forEach(function (el) { setText(el, fmt(p.redeem_step)); });
      $all('[data-kkcl="redeem-val"]').forEach(function (el) { setText(el, money(p.redeem_dollars)); });
    }
    var v = viewer();
    var amounts = {
      second_order: p.second_order_bonus,
      referral: p.referral_bonus,
      review: p.review_bonus,
      birthday: (v && v.tier.birthday_bonus) || p.birthday_bonus,
      fandom: p.fandom_profile_bonus,
    };
    $all('[data-kkcl-amount]').forEach(function (el) {
      var k = el.getAttribute('data-kkcl-amount');
      if (amounts[k] != null && amounts[k] !== '') setText(el, fmt(amounts[k]));
    });
    // Pockets: live member count.
    if (prog.members > 0) {
      $all('[data-kkcl="members"]').forEach(function (el) { setText(el, fmt(prog.members)); });
      $all('[data-kkcl-stat="members"]').forEach(function (el) { show(el, true); });
    }
    $all('[data-kkcl="stats"]').forEach(function (strip) {
      var n = $all(':scope > div', strip).filter(function (d) { return !d.hidden; }).length;
      strip.style.setProperty('--kkcl-stat-cols', String(Math.max(n, 1)));
      show(strip, n > 0);
      var note = strip.parentNode.querySelector('[data-kkcl="stats-note"]');
      show(note, n > 0);
    });
  }

  /* ----------------------------------------------------------- rewards */
  function applyRewards(root) {
    var t = tiers();
    if (state.options && state.options.length) {
      $all('[data-kkcl-reward]', root).forEach(function (li) {
        var ref = li.getAttribute('data-ref');
        if (!ref) return;
        var hits = state.options.filter(function (o) { return o.id === ref || (o.group_key && o.group_key === ref); });
        var pts = hits.map(function (o) { return Number(o.points); }).filter(function (n) { return n > 0; });
        if (!pts.length) return;
        var lo = Math.min.apply(null, pts), hi = Math.max.apply(null, pts);
        li.setAttribute('data-cost', lo);
        li.setAttribute('data-max', hi);
        var rank = hits.reduce(function (r, o) { return Math.min(r, tierIndex(o.min_tier) + 1 || 1); }, Infinity);
        if (rank !== Infinity && t.length) li.setAttribute('data-tier', rank);
        setText(li.querySelector('[data-kkcl-cost]'), lo === hi ? fmt(lo) : fmt(lo) + '–' + fmt(hi));
      });
    }

    var m = state.member;
    var v = viewer();

    // Lock rewards above the viewer's tier (guests and non-members count as
    // the first tier): faded card + "Unlocks at <tier>" pill (CSS .is-locked).
    if (root.dataset.lockOn) {
      var mineRank = m && v ? v.idx + 1 : 1;
      $all('[data-kkcl-reward]', root).forEach(function (li) {
        var need = +li.getAttribute('data-tier') || 1;
        li.classList.toggle('is-locked', need > mineRank);
        var txt = li.querySelector('[data-kkcl-lock-txt]');
        var tier = t[need - 1];
        if (txt && tier && root.dataset.lockText) setText(txt, str(root.dataset.lockText, { tier: tier.name }));
      });
    }

    if (!m || !v) return; // guests keep the merchant's eligibility line
    var mine = v.idx + 1;
    var bal = Number(m.balance) || 0;
    var d = root.dataset;
    $all('[data-kkcl-reward]', root).forEach(function (li) {
      var req = li.querySelector('[data-kkcl-req]');
      var go = li.querySelector('[data-kkcl-go]');
      var need = +li.getAttribute('data-tier') || 1;
      var cost = +li.getAttribute('data-cost') || 0;
      var max = Math.max(+li.getAttribute('data-max') || 0, cost);
      var cls = '', text, canGo = false;
      if (mine < need) {
        var tier = t[need - 1];
        text = str(d.eligLocked, { tier: tier ? tier.name : '' });
        cls = 'is-lock';
      } else if (bal >= max) {
        text = d.eligOk; cls = 'is-ok'; canGo = true;
      } else if (bal >= cost) {
        text = d.eligSome; cls = 'is-ok'; canGo = true;
      } else {
        text = str(d.eligShort, { n: fmt(cost - bal) });
      }
      if (req) {
        req.classList.remove('is-ok', 'is-lock');
        if (cls) req.classList.add(cls);
        setText(req, text);
      }
      show(go, canGo);
    });
  }

  /* -------------------------------------------------------------- earn */
  function applyEarn(root) {
    var v = viewer();
    if (!state.program || !v) return;
    var d = root.dataset;
    var t = tiers();
    var rate = earnRate(v.tier);
    var isMember = !!state.member;
    setText(root.querySelector('[data-kkcl="rate"]'), fmtRate(rate));
    var label = str(isMember ? d.rateLabelMember : d.rateLabelGuest, { tier: v.tier.name });
    setText(root.querySelector('[data-kkcl="rate-label"]'), label);
    var wrap = root.querySelector('[data-kkcl="rate-wrap"]');
    if (wrap) wrap.setAttribute('aria-label', str(d.rateAria, { rate: fmtRate(rate), label: label }));

    var order = Number(d.exampleOrder) || 0;
    var pts = Math.round(order * rate);
    var val = pointsToDollars(pts);
    setText(root.querySelector('[data-kkcl="eg-order"]'), money(order));
    setText(root.querySelector('[data-kkcl="eg-pts"]'), fmt(pts));
    if (val != null) setText(root.querySelector('[data-kkcl="eg-val"]'), money(Math.round(val * 100) / 100));

    // Tier pills: only tiers above the viewer, never a lower tier's rate.
    var pills = root.querySelector('[data-kkcl="pills"]');
    var top = root.querySelector('[data-kkcl="top-note"]');
    var above = t.slice(v.idx + 1);
    if (pills) {
      pills.textContent = '';
      above.forEach(function (tier) {
        var pct = Math.round(((Number(tier.multiplier) || 1) / (Number(v.tier.multiplier) || 1) - 1) * 100);
        if (pct <= 0) return;
        var a = node('a', 'kkcl-pill');
        a.href = '#tiers';
        a.appendChild(node('b', null, tier.name));
        var rest = node('span');
        tpl(rest, v.idx === 0 ? d.pillVsBase : d.pillVsMine, { pct: node('em', null, '+' + pct + '%'), tier: v.tier.name });
        a.appendChild(rest);
        pills.appendChild(a);
      });
      if (d.pillsAria) pills.setAttribute('aria-label', d.pillsAria);
      show(pills, pills.children.length > 0);
    }
    show(top, isMember && above.length === 0);

    // Birthday status (members, only once /balance answered).
    var set = root.querySelector('[data-kkcl="bday-set"]');
    var add = root.querySelector('[data-kkcl="bday-add"]');
    if (isMember && (set || add)) {
      var b = state.member.birthday;
      var mm = b && /^(\d{2})-(\d{2})$/.exec(b);
      if (mm && set) {
        var months = String(d.monthNames || '').split(',');
        var month = (months[+mm[1] - 1] || '').trim();
        tpl(set, d.bdaySet, { date: (+mm[2]) + (month ? ' ' + month : '') });
        show(set, true);
        show(add, false);
      } else if (!b) {
        show(set, false);
        show(add, true);
      }
    }
  }

  // Stamp card panel: only while the punch card is on (/program → punch).
  // Guests see an example card, members their own stamps.
  function applyStamp(root) {
    var box = root.querySelector('[data-kkcl="stamp"]');
    if (!box || !window.KKStamps) return;
    var d = root.dataset;
    var pp = state.program && state.program.punch;
    if (!(pp && pp.enabled) && d.design === 'true') pp = { enabled: true, per_card: 5, min_order_cents: 0, reward_label: '$5 off' };
    if (!(pp && pp.enabled)) { show(box, false); return; }
    var total = Number(pp.per_card) || 0;
    var mine = state.member && state.card && state.card.punch && state.card.punch.enabled ? state.card.punch : null;
    // A member's own card may pay a personal reward (Oracle's pick): /card has it.
    var label = (mine && mine.reward_label) || pp.reward_label;
    var p = { per_card: total, count: mine ? mine.count : Math.min(3, Math.max(0, total - 1)), reward_label: label };
    window.KKStamps.draw(root.querySelector('[data-kkcl="stamps"]'), p);
    var min = Number(pp.min_order_cents) || 0;
    var text = str(label ? d.stampText : d.stampTextPlain, { total: total, reward: label || '' });
    if (min > 0 && d.stampMin) text += ' ' + str(d.stampMin, { min: money(min / 100) });
    setText(root.querySelector('[data-kkcl="stamp-text"]'), text);
    var line = root.querySelector('[data-kkcl="stamp-mine"]');
    if (line) {
      var v = window.KKStamps.vars(p);
      if (mine && d.stampMine) setText(line, str(d.stampMine, { count: v.count, total: v.total, left: v.left }));
      show(line, !!(mine && d.stampMine));
    }
    show(box, true);
  }

  /* ------------------------------------------------------------- refer */
  function applyRefer(root) {
    var d = root.dataset;
    var p = state.program && state.program.points;
    var split = root.querySelector('[data-kkcl="split"]');
    if (split && p && p.referral_bonus) split.setAttribute('aria-label', str(d.splitAria, { points: fmt(p.referral_bonus) }));
  }

  function loadReferral(root) {
    var box = root.querySelector('[data-kkcl="rlink"]');
    var fallback = root.querySelector('[data-kkcl="rlink-fallback"]');
    if (!box) return;
    get('/referral').then(function (r) {
      var url = r && r.referral_url;
      if (!url) { show(fallback, true); return; }
      var input = box.querySelector('[data-kkcl="ref-url"]');
      input.value = url;
      var stat = box.querySelector('[data-kkcl="ref-stat"]');
      if (stat && r.friends_joined != null && r.points_earned != null) {
        tpl(stat, stat.getAttribute('data-template'), { friends: node('b', null, fmt(r.friends_joined)), points: node('b', null, fmt(r.points_earned)) });
        show(stat, true);
      }
      show(box, true);
      bindReferral(root, box, input);
    }).catch(function () { show(fallback, true); });
  }

  function bindReferral(root, box, input) {
    if (box.dataset.kkBound) return;
    box.dataset.kkBound = '1';
    var d = root.dataset;
    var copyBtn = box.querySelector('[data-kkcl="ref-copy"]');
    var copyTxt = box.querySelector('[data-kkcl="ref-copy-txt"]');
    var live = box.querySelector('[data-kkcl="ref-live"]');
    var original = copyTxt ? copyTxt.textContent : '';
    var timer = null;
    // Instant action: a "Copied" label, not a busy spinner (FRONTEND.md).
    function copied() {
      setText(copyTxt, d.copied || original);
      if (live) { live.textContent = ''; live.textContent = d.copiedLive || ''; }
      clearTimeout(timer);
      timer = setTimeout(function () { setText(copyTxt, original); }, 1800);
    }
    function legacy() {
      input.focus(); input.select();
      try { document.execCommand('copy'); } catch (e) { /* the selection is still there to copy by hand */ }
      copied();
    }
    function copy() {
      // The shared copy primitive (assets/kkumi-copy.js); the legacy path is only the last resort.
      window.KKCopy.write(input.value).then(function (ok) { if (ok) copied(); else legacy(); });
    }
    if (copyBtn) copyBtn.addEventListener('click', copy);
    var share = box.querySelector('[data-kkcl="ref-share"]');
    if (share) share.addEventListener('click', function () {
      var p = state.program && state.program.points;
      if (navigator.share) {
        navigator.share({
          title: d.shareTitle || '',
          text: str(d.shareText, { points: p && p.referral_bonus ? fmt(p.referral_bonus) : '' }),
          url: input.value,
        }).catch(function () { /* dismissed */ });
      } else copy();
    });
  }

  /* ------------------------------------------------------------- tiers */
  function applyTiers(root) {
    var t = tiers();
    if (!t.length) return;
    var d = root.dataset;
    $all('[data-kkcl-tier]', root).forEach(function (th) {
      var tier = t[+th.getAttribute('data-kkcl-tier')];
      if (!tier) return;
      setText(th.querySelector('[data-kkcl="tier-name"]'), tier.name);
      var cents = Number(tier.min_spend_cents) || 0;
      setText(th.querySelector('[data-kkcl="tier-need"]'), cents > 0 ? str(d.needSpend, { amount: money(cents / 100) }) : d.needFirst);
      setText(th.querySelector('[data-kkcl="tier-rate"]'), fmtRate(earnRate(tier)));
    });
    $all('[data-kkcl-tab]', root).forEach(function (b) {
      var tier = t[+b.getAttribute('data-kkcl-tab')];
      if (tier) setText(b.querySelector('[data-kkcl="tier-tab"]'), tier.name);
    });
    // Perk-cell tokens: program-wide amounts, and each column's own birthday
    // bonus (a tier without one gets the base bonus, as the cron pays it).
    var p = state.program.points || {};
    var flat = { second_order: p.second_order_bonus, referral: p.referral_bonus, review: p.review_bonus, review_media: p.review_media_bonus };
    $all('[data-kkcl-tok]', root).forEach(function (el) {
      var k = el.getAttribute('data-kkcl-tok');
      var v = k === 'birthday'
        ? ((t[+el.getAttribute('data-col')] || {}).birthday_bonus || p.birthday_bonus)
        : flat[k];
      if (v != null && v !== '') setText(el, fmt(v));
    });
    var v = state.member && viewer();
    if (!v) return;
    $all('[data-kkcl-tier]', root).forEach(function (th) {
      th.classList.toggle('is-mine', +th.getAttribute('data-kkcl-tier') === v.idx);
    });
    if (!root.dataset.kkclPicked) showTierCol(root, v.idx);
    var atTop = v.idx === t.length - 1;
    var ledeTop = root.querySelector('[data-kkcl="tiers-lede-top"]');
    if (ledeTop) {
      show(ledeTop, atTop);
      show(root.querySelector('[data-kkcl="tiers-lede"]'), !atTop);
    }
  }

  /* Phone tier switcher (< 640px): show one column of the comparison table. */
  function showTierCol(root, idx) {
    var table = root.querySelector('.kkcl-tiers__table');
    if (!table) return;
    table.setAttribute('data-kkcl-col', String(idx));
    $all('[data-kkcl-tab]', root).forEach(function (b) {
      b.setAttribute('aria-pressed', String(+b.getAttribute('data-kkcl-tab') === idx));
    });
  }
  function bindTierTabs(root) {
    var tabs = root.querySelector('[data-kkcl="tier-tabs"]');
    if (!tabs || tabs.dataset.kkBound) return;
    tabs.dataset.kkBound = '1';
    tabs.addEventListener('click', function (e) {
      var b = e.target.closest('[data-kkcl-tab]');
      if (!b) return;
      root.dataset.kkclPicked = '1'; // the shopper chose; don't jump to their tier later
      showTierCol(root, +b.getAttribute('data-kkcl-tab'));
    });
  }

  /* ------------------------------------------------------------ sticky */
  function applySticky(root) {
    var v = viewer();
    if (!state.member) {
      // Guests: the first tier's earn rate.
      var t = tiers();
      if (t.length) $all('[data-kkcl="rate"]', root).forEach(function (el) { setText(el, fmtRate(earnRate(t[0]))); });
      return;
    }
    var txt = root.querySelector('[data-kkcl="sticky-member"]');
    if (txt && state.member.tier) {
      setText(txt, str(root.dataset.memberText, { points: fmt(state.member.balance), tier: (v && v.tier.name) || state.member.tier.name || '' }));
    }
  }

  function bindSticky(bar) {
    if (bar.dataset.kkBound || !('IntersectionObserver' in window)) return;
    bar.dataset.kkBound = '1';
    document.body.classList.add('kkcl-has-sticky');
    var start = document.querySelector('[data-kkcl-sticky-start]');
    if (!start) return; // nothing to measure from: stay hidden
    var past = false;
    var vis = new Map();
    var links = $all('a, button', bar);
    function update() {
      var blocked = false;
      vis.forEach(function (on) { if (on) blocked = true; });
      var off = !past || blocked;
      bar.classList.toggle('is-off', off);
      document.body.classList.toggle('kkcl-sticky-on', !off); // lifts the club launcher clear of the bar
      bar.setAttribute('aria-hidden', String(off));
      links.forEach(function (a) { a.tabIndex = off ? -1 : 0; });
    }
    new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { past = !e.isIntersecting && e.boundingClientRect.top < 0; });
      update();
    }).observe(start);
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { vis.set(e.target, e.isIntersecting); });
      update();
    }, { rootMargin: '0px 0px 80px 0px' });
    $all('[data-kkcl-sticky-hide], .kk-fq-sec, .kk-faq, footer').forEach(function (el) {
      if (!bar.contains(el)) io.observe(el);
    });
  }

  /* ------------------------------------------- signed in, not in the Club */
  function applyNonMember() {
    if (!state.nonMember) return;
    $all('[data-kkcl-member-only]').forEach(function (el) { show(el, false); });
    $all('[data-kkcl-nonmember-only]').forEach(function (el) { show(el, true); });
    // kkumi-club-faq: guest questions (e.g. "How do I join?") apply again,
    // in the signed-out order.
    $all('[data-kkcl-faq-guest]').forEach(function (el) { show(el, true); });
    $all('[data-kkcl-faq-order]').forEach(function (el) { el.style.order = ''; });
  }

  /* --------------------------------------------------------- orchestrate */
  function each(name, fn) {
    $all('[data-kkcl-section="' + name + '"]').forEach(function (root) {
      try { fn(root); } catch (e) { /* one section failing never blanks the others */ }
    });
  }
  function render() {
    applyNonMember();
    applyProgram();
    each('rewards', applyRewards);
    each('earn', applyEarn);
    each('earn', applyStamp);
    each('refer', applyRefer);
    each('tiers', applyTiers);
    each('sticky', applySticky);
  }

  function boot() {
    var member = isMemberPage();
    var needsOptions = $all('[data-kkcl-reward][data-ref]').some(function (li) { return li.getAttribute('data-ref'); });
    var jobs = [
      get('/program').then(function (p) { state.program = p; }).catch(function () {}),
    ];
    if (member) {
      jobs.push(get('/balance').then(function (b) {
        var signedIn = !!(b && b.logged_in);
        state.nonMember = signedIn && b.club_member === false;
        state.member = signedIn && !state.nonMember ? b : null;
      }).catch(function () { state.balanceFailed = true; }));
    }
    if (member && document.querySelector('[data-kkcl="stamp"]')) {
      jobs.push(get('/card').then(function (c) { state.card = c; }).catch(function () {}));
    }
    if (needsOptions) {
      jobs.push(get('/options').then(function (o) { state.options = (o && o.options) || null; }).catch(function () {}));
    }
    Promise.all(jobs).then(function () {
      render();
      // A non-member has no link (the worker answers 403 club_required); if
      // /balance itself failed, try anyway: a failure shows the fallback link.
      if (state.member || state.balanceFailed) each('refer', loadReferral);
    });
    each('sticky', bindSticky);
    each('tiers', bindTierTabs);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor: a re-rendered section comes back with Liquid fallbacks.
  document.addEventListener('shopify:section:load', function () {
    render();
    if (state.member || state.balanceFailed) each('refer', loadReferral);
    each('sticky', bindSticky);
    each('tiers', bindTierTabs);
  });
})();
